import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Collapse,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  LinearProgress,
  Tabs,
  Tab,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Delete as DeleteIcon,
  CheckCircle as KeepIcon,
  MergeType as MergeIcon,
  Restaurant as RecipeIcon,
  CleaningServices as CleanAllIcon,
  AutoFixHigh as AutoMergeIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  getDuplicateRecipes,
  getFuzzyDuplicates,
  cleanupDuplicates,
  deleteRecipe,
  getRecipe,
  DuplicateGroup,
  DuplicateRecipe,
  FuzzyDuplicateGroup,
  Recipe,
} from '../adminApi';

type ViewMode = 'exact' | 'fuzzy';
type GroupFilter = 'all' | 'needs_review' | 'obvious';

const DuplicatesList: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('exact');

  // Exact match state
  const [exactGroups, setExactGroups] = useState<DuplicateGroup[]>([]);
  const [exactStats, setExactStats] = useState({ totalGroups: 0, totalDuplicates: 0 });

  // Fuzzy match state
  const [fuzzyGroups, setFuzzyGroups] = useState<FuzzyDuplicateGroup[]>([]);
  const [fuzzyStats, setFuzzyStats] = useState({ totalGroups: 0, totalDuplicates: 0 });
  const [fuzzyThreshold, setFuzzyThreshold] = useState(0.5);
  const [pendingThreshold, setPendingThreshold] = useState(0.5);

  // Shared state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [cleanupInProgress, setCleanupInProgress] = useState<string | null>(null);

  // Batch selection state (group-level checkboxes)
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [batchInProgress, setBatchInProgress] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, currentName: '' });

  // Confirmation dialog state for merge all
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    group: DuplicateGroup | null;
    keepRecipe: DuplicateRecipe | null;
  }>({ open: false, group: null, keepRecipe: null });

  // Batch confirmation dialog
  const [batchConfirmDialog, setBatchConfirmDialog] = useState(false);

  // Confirmation dialog state for individual delete
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    recipe: DuplicateRecipe | null;
  }>({ open: false, recipe: null });
  const [deleteInProgress, setDeleteInProgress] = useState<number | null>(null);

  // Selective merge state — per-group recipe selection
  const [selectedRecipes, setSelectedRecipes] = useState<Map<string, Set<number>>>(new Map());

  // Selective merge confirmation dialog
  const [selectiveMergeDialog, setSelectiveMergeDialog] = useState<{
    open: boolean;
    group: DuplicateGroup | null;
    keepRecipe: DuplicateRecipe | null;
    deleteIds: number[];
  }>({ open: false, group: null, keepRecipe: null, deleteIds: [] });

  // Recipe preview dialog state
  const [previewRecipeId, setPreviewRecipeId] = useState<number | null>(null);
  const [previewRecipe, setPreviewRecipe] = useState<Recipe | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Group filter state
  const [groupFilter, setGroupFilter] = useState<GroupFilter>('all');

  // Auto-merge confirmation dialog
  const [autoMergeConfirmDialog, setAutoMergeConfirmDialog] = useState(false);

  const fetchExactDuplicates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDuplicateRecipes();
      setExactGroups(data.groups);
      setExactStats({ totalGroups: data.total_groups, totalDuplicates: data.total_duplicates });
      setSelectedGroups(new Set());
      setSelectedRecipes(new Map());
    } catch (err: any) {
      setError(err.message || 'Failed to load duplicates');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFuzzyDuplicates = useCallback(async (threshold?: number) => {
    setLoading(true);
    setError(null);
    try {
      const t = threshold ?? fuzzyThreshold;
      const data = await getFuzzyDuplicates(t);
      setFuzzyGroups(data.groups);
      setFuzzyStats({ totalGroups: data.total_groups, totalDuplicates: data.total_duplicates });
      setFuzzyThreshold(t);
      setSelectedGroups(new Set());
      setSelectedRecipes(new Map());
    } catch (err: any) {
      setError(err.message || 'Failed to load fuzzy duplicates');
    } finally {
      setLoading(false);
    }
  }, [fuzzyThreshold]);

  useEffect(() => {
    fetchExactDuplicates();
  }, [fetchExactDuplicates]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: ViewMode) => {
    setViewMode(newValue);
    setSearchQuery('');
    setExpandedGroups(new Set());
    setSelectedGroups(new Set());
    setSelectedRecipes(new Map());
    setGroupFilter('all');
    if (newValue === 'fuzzy' && fuzzyGroups.length === 0) {
      fetchFuzzyDuplicates();
    }
  };

  // Current groups/stats based on view mode
  const groups = viewMode === 'exact' ? exactGroups : fuzzyGroups;
  const stats = viewMode === 'exact' ? exactStats : fuzzyStats;

  // Helper: is this group "obvious" (safe to auto-merge)?
  const isObviousGroup = useCallback((g: DuplicateGroup): boolean => {
    const keeper = g.recipes.find(r => r.recipe_id === g.recommended_keep);
    return !!keeper && keeper.ingredient_count > 0 &&
      g.recipes.filter(r => r.recipe_id !== keeper.recipe_id).every(r => r.ingredient_count === 0);
  }, []);

  // Filter groups by search query + group filter
  const filteredGroups = useMemo(() => {
    let result = groups;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (g) =>
          g.normalized_name.includes(query) ||
          g.recipes.some((r) => r.name.toLowerCase().includes(query))
      );
    }

    if (groupFilter === 'obvious') {
      result = result.filter(isObviousGroup);
    } else if (groupFilter === 'needs_review') {
      result = result.filter(g => !isObviousGroup(g));
    }

    return result;
  }, [groups, searchQuery, groupFilter, isObviousGroup]);

  const obviousCount = useMemo(() => groups.filter(isObviousGroup).length, [groups, isObviousGroup]);
  const needsReviewCount = useMemo(() => groups.length - obviousCount, [groups.length, obviousCount]);

  const allFilteredSelected = filteredGroups.length > 0 && filteredGroups.every(g => selectedGroups.has(g.normalized_name));

  const toggleExpand = (normalizedName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(normalizedName)) {
        next.delete(normalizedName);
      } else {
        next.add(normalizedName);
      }
      return next;
    });
  };

  const toggleSelectGroup = (normalizedName: string) => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(normalizedName)) {
        next.delete(normalizedName);
      } else {
        next.add(normalizedName);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedGroups(new Set());
    } else {
      setSelectedGroups(new Set(filteredGroups.map(g => g.normalized_name)));
    }
  };

  // Toggle a recipe checkbox within a group (selective merge)
  const toggleSelectRecipe = (groupName: string, recipeId: number) => {
    setSelectedRecipes(prev => {
      const next = new Map(prev);
      const groupSet = new Set(prev.get(groupName) || []);
      if (groupSet.has(recipeId)) {
        groupSet.delete(recipeId);
      } else {
        groupSet.add(recipeId);
      }
      if (groupSet.size === 0) {
        next.delete(groupName);
      } else {
        next.set(groupName, groupSet);
      }
      return next;
    });
  };

  // --- Merge All ---
  const handleCleanup = async (group: DuplicateGroup, keepRecipe: DuplicateRecipe) => {
    setConfirmDialog({ open: true, group, keepRecipe });
  };

  const executeCleanup = async () => {
    const { group, keepRecipe } = confirmDialog;
    if (!group || !keepRecipe) return;

    setConfirmDialog({ open: false, group: null, keepRecipe: null });
    setCleanupInProgress(group.normalized_name);
    setError(null);
    setSuccess(null);

    try {
      const deleteIds = group.recipes
        .filter((r) => r.recipe_id !== keepRecipe.recipe_id)
        .map((r) => r.recipe_id);

      const result = await cleanupDuplicates(keepRecipe.recipe_id, deleteIds);

      let message = `Kept "${result.kept_recipe.name}" (#${result.kept_recipe.recipe_id}), deleted ${result.deleted_recipes} duplicates`;
      if (result.skipped_recipes > 0) {
        message += `. Skipped ${result.skipped_recipes} in active plans (will need cleanup later)`;
      }
      setSuccess(message);

      if (viewMode === 'exact') {
        await fetchExactDuplicates();
      } else {
        await fetchFuzzyDuplicates();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to cleanup duplicates');
    } finally {
      setCleanupInProgress(null);
    }
  };

  // --- Selective Merge ---
  const handleSelectiveMerge = (group: DuplicateGroup) => {
    const keepRecipe = group.recipes.find(r => r.recipe_id === group.recommended_keep);
    const selected = selectedRecipes.get(group.normalized_name);
    if (!keepRecipe || !selected || selected.size === 0) return;
    setSelectiveMergeDialog({
      open: true,
      group,
      keepRecipe,
      deleteIds: Array.from(selected),
    });
  };

  const executeSelectiveMerge = async () => {
    const { group, keepRecipe, deleteIds } = selectiveMergeDialog;
    if (!group || !keepRecipe || deleteIds.length === 0) return;

    setSelectiveMergeDialog({ open: false, group: null, keepRecipe: null, deleteIds: [] });
    setCleanupInProgress(group.normalized_name);
    setError(null);
    setSuccess(null);

    try {
      const result = await cleanupDuplicates(keepRecipe.recipe_id, deleteIds);

      let message = `Kept "${result.kept_recipe.name}" (#${result.kept_recipe.recipe_id}), deleted ${result.deleted_recipes} selected duplicates`;
      if (result.skipped_recipes > 0) {
        message += `. Skipped ${result.skipped_recipes} in active plans`;
      }
      setSuccess(message);

      setSelectedRecipes(prev => {
        const next = new Map(prev);
        next.delete(group.normalized_name);
        return next;
      });

      if (viewMode === 'exact') {
        await fetchExactDuplicates();
      } else {
        await fetchFuzzyDuplicates();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to cleanup duplicates');
    } finally {
      setCleanupInProgress(null);
    }
  };

  // --- Batch Cleanup ---
  const executeBatchCleanup = async () => {
    setBatchConfirmDialog(false);
    const targetGroups = filteredGroups.filter(g => selectedGroups.has(g.normalized_name));
    if (targetGroups.length === 0) return;

    setBatchInProgress(true);
    setBatchProgress({ current: 0, total: targetGroups.length, currentName: '' });
    setError(null);
    setSuccess(null);

    let totalDeleted = 0;
    let totalSkipped = 0;
    let failures = 0;

    for (let i = 0; i < targetGroups.length; i++) {
      const group = targetGroups[i];
      const keepRecipe = group.recipes.find(r => r.recipe_id === group.recommended_keep);
      if (!keepRecipe) continue;

      setBatchProgress({ current: i + 1, total: targetGroups.length, currentName: group.normalized_name });

      try {
        const deleteIds = group.recipes
          .filter(r => r.recipe_id !== keepRecipe.recipe_id)
          .map(r => r.recipe_id);

        const result = await cleanupDuplicates(keepRecipe.recipe_id, deleteIds);
        totalDeleted += result.deleted_recipes;
        totalSkipped += result.skipped_recipes;
      } catch (err) {
        failures++;
      }
    }

    setBatchInProgress(false);
    setBatchProgress({ current: 0, total: 0, currentName: '' });

    let message = `Batch cleanup complete: ${totalDeleted} duplicates deleted across ${targetGroups.length - failures} groups`;
    if (totalSkipped > 0) message += `, ${totalSkipped} skipped (in active plans)`;
    if (failures > 0) message += `, ${failures} groups failed`;
    setSuccess(message);

    if (viewMode === 'exact') {
      await fetchExactDuplicates();
    } else {
      await fetchFuzzyDuplicates();
    }
  };

  // --- Auto-Merge Obvious Groups ---
  const executeAutoMerge = async () => {
    setAutoMergeConfirmDialog(false);
    const targets = groups.filter(isObviousGroup);
    if (targets.length === 0) return;

    setBatchInProgress(true);
    setBatchProgress({ current: 0, total: targets.length, currentName: '' });
    setError(null);
    setSuccess(null);

    let totalDeleted = 0;
    let totalSkipped = 0;
    let failures = 0;

    for (let i = 0; i < targets.length; i++) {
      const group = targets[i];
      const keepRecipe = group.recipes.find(r => r.recipe_id === group.recommended_keep);
      if (!keepRecipe) continue;

      setBatchProgress({ current: i + 1, total: targets.length, currentName: group.normalized_name });

      try {
        const deleteIds = group.recipes
          .filter(r => r.recipe_id !== keepRecipe.recipe_id)
          .map(r => r.recipe_id);

        const result = await cleanupDuplicates(keepRecipe.recipe_id, deleteIds);
        totalDeleted += result.deleted_recipes;
        totalSkipped += result.skipped_recipes;
      } catch (err) {
        failures++;
      }
    }

    setBatchInProgress(false);
    setBatchProgress({ current: 0, total: 0, currentName: '' });

    let message = `Auto-merge complete: ${totalDeleted} stubs deleted across ${targets.length - failures} obvious groups`;
    if (totalSkipped > 0) message += `, ${totalSkipped} skipped (in active plans)`;
    if (failures > 0) message += `, ${failures} groups failed`;
    setSuccess(message);

    if (viewMode === 'exact') {
      await fetchExactDuplicates();
    } else {
      await fetchFuzzyDuplicates();
    }
  };

  // --- Single Delete ---
  const handleDeleteSingle = (recipe: DuplicateRecipe) => {
    setDeleteDialog({ open: true, recipe });
  };

  const executeDeleteSingle = async () => {
    const { recipe } = deleteDialog;
    if (!recipe) return;

    setDeleteDialog({ open: false, recipe: null });
    setDeleteInProgress(recipe.recipe_id);
    setError(null);
    setSuccess(null);

    try {
      await deleteRecipe(recipe.recipe_id, 'Duplicate cleanup', true);
      setSuccess(`Permanently deleted recipe "${recipe.name}" (ID: ${recipe.recipe_id})`);
      if (viewMode === 'exact') {
        await fetchExactDuplicates();
      } else {
        await fetchFuzzyDuplicates();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete recipe');
    } finally {
      setDeleteInProgress(null);
    }
  };

  // --- Recipe Preview ---
  const openPreview = async (recipeId: number) => {
    setPreviewRecipeId(recipeId);
    setPreviewLoading(true);
    setPreviewRecipe(null);
    try {
      const recipe = await getRecipe(recipeId);
      setPreviewRecipe(recipe);
    } catch (err: any) {
      setError(err.message || 'Failed to load recipe preview');
      setPreviewRecipeId(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewRecipeId(null);
    setPreviewRecipe(null);
    setPreviewLoading(false);
  };

  if (loading && groups.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Duplicate Recipes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {stats.totalGroups} {viewMode === 'fuzzy' ? 'similar' : 'duplicate'} groups, {stats.totalDuplicates} total duplicates
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {obviousCount > 0 && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AutoMergeIcon />}
              onClick={() => setAutoMergeConfirmDialog(true)}
              disabled={batchInProgress || loading}
            >
              Auto-merge {obviousCount} obvious
            </Button>
          )}
          {selectedGroups.size > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CleanAllIcon />}
              onClick={() => setBatchConfirmDialog(true)}
              disabled={batchInProgress}
            >
              Clean {selectedGroups.size === filteredGroups.length ? 'All' : selectedGroups.size} {selectedGroups.size === 1 ? 'Group' : 'Groups'}
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => viewMode === 'exact' ? fetchExactDuplicates() : fetchFuzzyDuplicates()}
            disabled={loading || batchInProgress}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={viewMode} onChange={handleTabChange}>
          <Tab label="Exact Matches" value="exact" />
          <Tab label="Similar Recipes" value="fuzzy" />
        </Tabs>
      </Paper>

      {/* Fuzzy threshold slider */}
      {viewMode === 'fuzzy' && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Similarity Threshold: {pendingThreshold.toFixed(2)}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="caption" color="text.secondary">0.30</Typography>
            <Slider
              value={pendingThreshold}
              onChange={(_, v) => setPendingThreshold(v as number)}
              onChangeCommitted={(_, v) => fetchFuzzyDuplicates(v as number)}
              min={0.3}
              max={0.7}
              step={0.05}
              valueLabelDisplay="auto"
              sx={{ flexGrow: 1 }}
            />
            <Typography variant="caption" color="text.secondary">0.70</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Lower = more matches (looser), Higher = fewer matches (stricter)
          </Typography>
        </Paper>
      )}

      {/* Batch progress */}
      {batchInProgress && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            Cleaning up {batchProgress.current}/{batchProgress.total}: {batchProgress.currentName}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(batchProgress.current / batchProgress.total) * 100}
          />
        </Paper>
      )}

      {/* Search + Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by recipe name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Showing {filteredGroups.length} of {groups.length} groups
            </Typography>
            <ToggleButtonGroup
              value={groupFilter}
              exclusive
              onChange={(_, v) => { if (v) setGroupFilter(v); }}
              size="small"
            >
              <ToggleButton value="all">All ({groups.length})</ToggleButton>
              <ToggleButton value="needs_review">Needs Review ({needsReviewCount})</ToggleButton>
              <ToggleButton value="obvious">Obvious ({obviousCount})</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          {filteredGroups.length > 0 && (
            <Button size="small" onClick={toggleSelectAll} disabled={batchInProgress}>
              {allFilteredSelected ? 'Deselect All' : 'Select All'}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Loading overlay */}
      {loading && groups.length > 0 && (
        <Box display="flex" justifyContent="center" mb={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Duplicate Groups */}
      {filteredGroups.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <RecipeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {searchQuery ? 'No matching duplicates found' : viewMode === 'fuzzy' ? 'No similar recipes found at this threshold' : 'No duplicates found'}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {filteredGroups.map((group) => {
            const isExpanded = expandedGroups.has(group.normalized_name);
            const isProcessing = cleanupInProgress === group.normalized_name;
            const isSelected = selectedGroups.has(group.normalized_name);
            const recommendedRecipe = group.recipes.find(
              (r) => r.recipe_id === group.recommended_keep
            );
            const fuzzyGroup = group as FuzzyDuplicateGroup;
            const groupSelectedCount = selectedRecipes.get(group.normalized_name)?.size || 0;

            return (
              <Paper key={group.normalized_name} sx={{ overflow: 'hidden', opacity: batchInProgress ? 0.6 : 1 }}>
                {/* Group Header */}
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: isExpanded ? 'action.selected' : 'transparent',
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={() => toggleSelectGroup(group.normalized_name)}
                    disabled={batchInProgress}
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                  />

                  <IconButton size="small" onClick={() => toggleExpand(group.normalized_name)}>
                    {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                  </IconButton>

                  <Box
                    flexGrow={1}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => toggleExpand(group.normalized_name)}
                  >
                    <Typography variant="subtitle1" fontWeight="medium">
                      {viewMode === 'fuzzy'
                        ? group.recipes.map(r => r.name).join(' / ')
                        : group.normalized_name}
                    </Typography>
                    <Stack direction="row" spacing={1} mt={0.5}>
                      <Chip
                        size="small"
                        label={`${group.count} recipes`}
                        color="primary"
                        variant="outlined"
                      />
                      {viewMode === 'fuzzy' && fuzzyGroup.similarity_score && (
                        <Chip
                          size="small"
                          label={`${(parseFloat(fuzzyGroup.similarity_score) * 100).toFixed(0)}% similar`}
                          color="secondary"
                          variant="outlined"
                        />
                      )}
                      {group.in_active_plans > 0 && (
                        <Chip
                          size="small"
                          label={`${group.in_active_plans} in active plans`}
                          color="warning"
                        />
                      )}
                      {isObviousGroup(group) && (
                        <Chip
                          size="small"
                          label="Obvious"
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Box>

                  <Stack direction="row" spacing={1}>
                    {groupSelectedCount > 0 && (
                      <Tooltip title="Merge only checked recipes">
                        <Button
                          variant="outlined"
                          size="small"
                          color="warning"
                          startIcon={<MergeIcon />}
                          disabled={isProcessing || batchInProgress}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectiveMerge(group);
                          }}
                        >
                          Merge {groupSelectedCount} Selected
                        </Button>
                      </Tooltip>
                    )}
                    {recommendedRecipe && (
                      <Tooltip title="Quick cleanup: keep recommended recipe, delete all others">
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={isProcessing ? <CircularProgress size={16} /> : <MergeIcon />}
                          disabled={isProcessing || batchInProgress}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCleanup(group, recommendedRecipe);
                          }}
                        >
                          {isProcessing ? 'Cleaning...' : 'Merge All'}
                        </Button>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>

                {/* Expanded Recipe List */}
                <Collapse in={isExpanded}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={40} padding="checkbox" />
                          <TableCell width={60}>ID</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell align="center">Ingredients</TableCell>
                          <TableCell align="center">Steps</TableCell>
                          <TableCell align="center">Used</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.recipes.map((recipe) => {
                          const isRecommended = recipe.recipe_id === group.recommended_keep;
                          const isRecipeSelected = selectedRecipes.get(group.normalized_name)?.has(recipe.recipe_id) || false;
                          return (
                            <TableRow
                              key={recipe.recipe_id}
                              sx={{
                                bgcolor: isRecommended ? 'success.light' : 'transparent',
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                            >
                              <TableCell padding="checkbox">
                                {!isRecommended && (
                                  <Checkbox
                                    size="small"
                                    checked={isRecipeSelected}
                                    onChange={() => toggleSelectRecipe(group.normalized_name, recipe.recipe_id)}
                                    disabled={isProcessing || batchInProgress}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontFamily="monospace">
                                  {recipe.recipe_id}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    sx={{
                                      cursor: 'pointer',
                                      color: 'primary.main',
                                      '&:hover': { textDecoration: 'underline' },
                                    }}
                                    onClick={() => openPreview(recipe.recipe_id)}
                                  >
                                    {recipe.name}
                                  </Typography>
                                  {isRecommended && (
                                    <Chip
                                      size="small"
                                      label="Recommended"
                                      color="success"
                                      icon={<KeepIcon />}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={recipe.ingredient_count}
                                  color={recipe.ingredient_count > 0 ? 'primary' : 'default'}
                                  variant={recipe.ingredient_count > 0 ? 'filled' : 'outlined'}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={recipe.step_count}
                                  color={recipe.step_count > 0 ? 'primary' : 'default'}
                                  variant={recipe.step_count > 0 ? 'filled' : 'outlined'}
                                />
                              </TableCell>
                              <TableCell align="center">{recipe.usage_count || 0}</TableCell>
                              <TableCell align="center">
                                {recipe.in_active_plan ? (
                                  <Chip size="small" label="Active Plan" color="warning" />
                                ) : recipe.is_expanded ? (
                                  <Chip size="small" label="Expanded" color="info" variant="outlined" />
                                ) : (
                                  <Chip size="small" label="Stub" variant="outlined" />
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                  <Tooltip title={isRecommended ? 'Keep this recipe' : 'Keep this instead'}>
                                    <IconButton
                                      size="small"
                                      color={isRecommended ? 'success' : 'default'}
                                      onClick={() => handleCleanup(group, recipe)}
                                      disabled={isProcessing || deleteInProgress === recipe.recipe_id || batchInProgress}
                                    >
                                      {isRecommended ? <KeepIcon /> : <MergeIcon />}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip
                                    title={
                                      recipe.in_active_plan
                                        ? 'Cannot delete - in active plan'
                                        : 'Delete this recipe only'
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDeleteSingle(recipe)}
                                        disabled={
                                          recipe.in_active_plan ||
                                          isProcessing ||
                                          deleteInProgress === recipe.recipe_id ||
                                          batchInProgress
                                        }
                                      >
                                        {deleteInProgress === recipe.recipe_id ? (
                                          <CircularProgress size={18} />
                                        ) : (
                                          <DeleteIcon />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Merge All Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, group: null, keepRecipe: null })}>
        <DialogTitle>Confirm Cleanup</DialogTitle>
        <DialogContent>
          {confirmDialog.group && confirmDialog.keepRecipe && (
            <>
              <Typography gutterBottom>
                Keep <strong>"{confirmDialog.keepRecipe.name}"</strong> (ID: {confirmDialog.keepRecipe.recipe_id})
              </Typography>
              <Typography gutterBottom>
                Delete <strong>{confirmDialog.group.count - 1}</strong> duplicate recipes
              </Typography>
              {confirmDialog.group.in_active_plans > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {confirmDialog.group.in_active_plans} recipe(s) are in active meal plans.
                  Meals will be reassigned to the kept recipe.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, group: null, keepRecipe: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={executeCleanup} startIcon={<MergeIcon />}>
            Confirm Cleanup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Selective Merge Confirmation Dialog */}
      <Dialog
        open={selectiveMergeDialog.open}
        onClose={() => setSelectiveMergeDialog({ open: false, group: null, keepRecipe: null, deleteIds: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Merge Selected Recipes</DialogTitle>
        <DialogContent>
          {selectiveMergeDialog.group && selectiveMergeDialog.keepRecipe && (
            <>
              <Typography gutterBottom>
                Keep <strong>"{selectiveMergeDialog.keepRecipe.name}"</strong> (ID: {selectiveMergeDialog.keepRecipe.recipe_id})
              </Typography>
              <Typography gutterBottom>
                Delete the following {selectiveMergeDialog.deleteIds.length} recipe(s):
              </Typography>
              <List dense>
                {selectiveMergeDialog.deleteIds.map(id => {
                  const recipe = selectiveMergeDialog.group!.recipes.find(r => r.recipe_id === id);
                  return recipe ? (
                    <ListItem key={id} disableGutters>
                      <ListItemText
                        primary={`"${recipe.name}" (ID: ${recipe.recipe_id})`}
                        secondary={`${recipe.ingredient_count} ingredients, ${recipe.step_count} steps`}
                      />
                    </ListItem>
                  ) : null;
                })}
              </List>
              {selectiveMergeDialog.group.recipes
                .filter(r => r.recipe_id !== selectiveMergeDialog.keepRecipe!.recipe_id && !selectiveMergeDialog.deleteIds.includes(r.recipe_id))
                .length > 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {selectiveMergeDialog.group.recipes
                    .filter(r => r.recipe_id !== selectiveMergeDialog.keepRecipe!.recipe_id && !selectiveMergeDialog.deleteIds.includes(r.recipe_id))
                    .length} unchecked recipe(s) will be kept.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectiveMergeDialog({ open: false, group: null, keepRecipe: null, deleteIds: [] })}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" onClick={executeSelectiveMerge} startIcon={<MergeIcon />}>
            Merge Selected
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Cleanup Confirmation Dialog */}
      <Dialog open={batchConfirmDialog} onClose={() => setBatchConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Batch Cleanup — {selectedGroups.size} Groups</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            For each group, the <strong>recommended recipe</strong> (most complete, highest usage) will be kept and all duplicates deleted.
          </Typography>
          <Typography gutterBottom>
            This will clean up <strong>{filteredGroups.filter(g => selectedGroups.has(g.normalized_name)).reduce((sum, g) => sum + g.count - 1, 0)}</strong> duplicate recipes across <strong>{selectedGroups.size}</strong> groups.
          </Typography>
          {filteredGroups.filter(g => selectedGroups.has(g.normalized_name)).some(g => g.in_active_plans > 0) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Some recipes are in active meal plans and will be skipped automatically.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchConfirmDialog(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={executeBatchCleanup} startIcon={<CleanAllIcon />}>
            Clean {selectedGroups.size} Groups
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, recipe: null })}>
        <DialogTitle>Permanently Delete Recipe</DialogTitle>
        <DialogContent>
          {deleteDialog.recipe && (
            <>
              <Typography gutterBottom>
                Are you sure you want to <strong>permanently delete</strong> "{deleteDialog.recipe.name}"?
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Recipe ID: {deleteDialog.recipe.recipe_id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {deleteDialog.recipe.ingredient_count} ingredients, {deleteDialog.recipe.step_count} steps
              </Typography>
              <Alert severity="error" sx={{ mt: 2 }}>
                This will permanently delete the recipe and all its data. This action cannot be undone.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, recipe: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={executeDeleteSingle} startIcon={<DeleteIcon />}>
            Permanently Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recipe Preview Dialog */}
      <Dialog
        open={previewRecipeId !== null}
        onClose={closePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="span">
              {previewRecipe?.name || 'Loading...'}
            </Typography>
            <IconButton onClick={closePreview} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {previewLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : previewRecipe ? (
            <Stack spacing={2}>
              {previewRecipe.description && (
                <Typography variant="body1" color="text.secondary">
                  {previewRecipe.description}
                </Typography>
              )}

              {previewRecipe.tags && previewRecipe.tags.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Dietary Tags</Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {previewRecipe.tags.map(tag => (
                      <Chip key={tag.recipe_tag_id} label={tag.tag_name} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              )}

              {previewRecipe.ingredients && previewRecipe.ingredients.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Ingredients ({previewRecipe.ingredients.length})
                  </Typography>
                  <List dense disablePadding>
                    {previewRecipe.ingredients.map(ing => (
                      <ListItem key={ing.recipe_ingredient_id} disableGutters sx={{ py: 0.25 }}>
                        <ListItemText
                          primary={`${ing.quantity} ${ing.unit} ${ing.name}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Alert severity="info" variant="outlined">No ingredients (stub recipe)</Alert>
              )}

              {previewRecipe.steps && previewRecipe.steps.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Steps ({previewRecipe.steps.length})
                  </Typography>
                  <List dense disablePadding>
                    {previewRecipe.steps
                      .sort((a, b) => a.step_number - b.step_number)
                      .map(step => (
                        <ListItem key={step.step_id} disableGutters sx={{ py: 0.25 }}>
                          <ListItemText
                            primary={`${step.step_number}. ${step.description}`}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                  </List>
                </Box>
              ) : (
                <Alert severity="info" variant="outlined">No steps</Alert>
              )}

              {previewRecipe.nutrition_info && Object.keys(previewRecipe.nutrition_info).length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Nutrition</Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {Object.entries(previewRecipe.nutrition_info).map(([key, value]) => (
                      <Chip
                        key={key}
                        label={`${key}: ${value}`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <Divider />
              <Stack direction="row" spacing={2}>
                <Typography variant="caption" color="text.secondary">
                  ID: {previewRecipe.recipe_id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Prep: {previewRecipe.prep_time} min
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Created: {new Date(previewRecipe.created_at).toLocaleDateString()}
                </Typography>
              </Stack>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Auto-Merge Confirmation Dialog */}
      <Dialog
        open={autoMergeConfirmDialog}
        onClose={() => setAutoMergeConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Auto-Merge Obvious Groups</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            This will automatically merge <strong>{obviousCount}</strong> groups where:
          </Typography>
          <List dense>
            <ListItem disableGutters>
              <ListItemText primary="The recommended keeper has ingredients (fully expanded)" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="All other recipes in the group are stubs (0 ingredients)" />
            </ListItem>
          </List>
          <Typography gutterBottom>
            In each group, stub recipes will be deleted and the enriched recipe will be kept.
          </Typography>
          <Alert severity="info" sx={{ mt: 1 }}>
            Recipes in active meal plans will be skipped automatically.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoMergeConfirmDialog(false)}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={executeAutoMerge} startIcon={<AutoMergeIcon />}>
            Auto-Merge {obviousCount} Groups
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default DuplicatesList;
